import { Link, useParams } from 'react-router-dom'
import { cn } from '@/lib/utils'
import type { SupportSectionSlug } from './sections'

type ArticleNode = wowaudit.SupportArticle & { children: ArticleNode[] }

const buildArticleTree = (
  articles: wowaudit.SupportArticle[],
): ArticleNode[] => {
  const byId = new Map<number, ArticleNode>()
  articles.forEach((a) => byId.set(a.id, { ...a, children: [] }))

  const roots: ArticleNode[] = []
  byId.forEach((node) => {
    if (node.parentId) {
      byId.get(node.parentId)?.children.push(node)
    } else {
      roots.push(node)
    }
  })
  return roots
}

const SupportSidebarLink = ({
  article,
  section,
  depth,
}: {
  article: ArticleNode
  section: SupportSectionSlug
  depth: number
}) => {
  const { slug } = useParams()
  const isActive = slug === article.slug

  return (
    <>
      <Link
        to={`/support/${section}/${article.slug}`}
        className={cn(
          'block rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent',
          isActive && 'bg-accent font-medium text-primary',
        )}
        style={{ paddingLeft: `${0.75 + depth * 0.75}rem` }}
      >
        {article.title}
      </Link>
      {article.children.map((child) => (
        <SupportSidebarLink
          key={child.id}
          article={child}
          section={section}
          depth={depth + 1}
        />
      ))}
    </>
  )
}

const SupportSidebar = ({
  section,
  articles,
}: {
  section: SupportSectionSlug
  articles: wowaudit.SupportArticle[]
}) => {
  const { slug } = useParams()
  const roots = buildArticleTree(articles)
  const intro = roots[0]
  const additionalRoots = roots.slice(1)
  const sidebarChildren = intro?.children ?? []

  if (!intro && additionalRoots.length === 0) return null

  return (
    <nav className='flex flex-col gap-0.5 shrink-0 w-full lg:w-52 lg:border-r lg:border-border lg:pr-8'>
      {intro ? (
        <Link
          to={`/support/${section}`}
          className={cn(
            'block rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-accent',
            !slug && 'bg-accent text-primary',
          )}
        >
          {intro.title}
        </Link>
      ) : null}
      {additionalRoots.map((article) => (
        <SupportSidebarLink
          key={article.id}
          article={article}
          section={section}
          depth={0}
        />
      ))}
      {sidebarChildren.map((article) => (
        <SupportSidebarLink
          key={article.id}
          article={article}
          section={section}
          depth={0}
        />
      ))}
    </nav>
  )
}

export default SupportSidebar
